import React, { useEffect, useRef, useState } from 'react';
import './Home.css';
import '../../styles/social-posts.css';
import { useAuth } from '../../contexts/authContext';
import supabase from '../../supabaseClient';
import Modal from 'react-modal';
import Comments from './Components/Comments';
import './HomeModal.css';
import PostReactions from './Components/PostReactions';
import { FaPaperclip, FaPaperPlane, FaRetweet, FaRegCommentDots, FaEdit, FaTrash, FaExternalLinkAlt, FaExpand, FaPlay, FaShareAlt, FaLink, FaEllipsisV } from 'react-icons/fa';
import HoverCard from '../../components/HoverCard';
import { fetchShareCount, addShare } from '../../api/postActions';
import { extractEmbedFromContent, getEmbedSrc } from '../../utils/embed';
import DockedComposer from '../../components/DockedComposer';
import { LinguaSnapsRow } from '../../components/LinguaSnaps';

const Home = () => {
  const { user } = useAuth();
  const [userName, setUserName] = useState('User');
  const [postContent, setPostContent] = useState('');
  const [feed, setFeed] = useState({ items: [], page: 1, pageSize: 20, total: 0 });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Persist last known user id to survive transient auth flips
  const lastUserIdRef = useRef(null);
  // Track which userIds we have already fetched for in this session
  const fetchedRef = useRef(new Set());
  // Composer enhancements
  const [postMediaFile, setPostMediaFile] = useState(null);
  const [postMediaPreview, setPostMediaPreview] = useState(null);
  const [postMediaType, setPostMediaType] = useState(null);
  const [localEmbedPreview, setLocalEmbedPreview] = useState(null);
  const postMediaInputRef = useRef();
  const [composerUploading, setComposerUploading] = useState(false);
  // Edit/Delete state
  const [editingPostId, setEditingPostId] = useState(null);
  const [editDraft, setEditDraft] = useState({ content: '', embed_url: '' });
  // Comments modal and my repost state
  const [repostedByMe, setRepostedByMe] = useState({});
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [activeCommentPostId, setActiveCommentPostId] = useState(null);
  const [replyToCommentId, setReplyToCommentId] = useState(null);
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  // Media/embed viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewer, setViewer] = useState({ type: null, url: null, title: '' });
  const [shareCounts, setShareCounts] = useState({});
  const [embedMenuOpen, setEmbedMenuOpen] = useState({});
  const embedMenuRef = useRef();
  const [ownerMenuOpen, setOwnerMenuOpen] = useState({});
  const [composerOpen, setComposerOpen] = useState(false);

  // getEmbedSrc now imported from shared utils
  
  // Close embed menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (embedMenuRef.current && !embedMenuRef.current.contains(e.target)) {
        setEmbedMenuOpen({});
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleEmbedMenu = (itemId) => {
    setEmbedMenuOpen(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const getEmbedPlatform = (url) => {
    if (/youtube|youtu\.be/i.test(url)) return 'YouTube';
    if (/soundcloud/i.test(url)) return 'SoundCloud';
    return 'Link';
  };

  const copyEmbedLink = (url) => {
    navigator.clipboard?.writeText(url);
    setEmbedMenuOpen({});
  };

  const viewOnPlatform = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setEmbedMenuOpen({});
  };
  
  useEffect(() => {
    if (user) {
      // Check different possible locations for the name
      if (user.user_metadata?.name) {
        setUserName(user.user_metadata.name);
      } else if (user.user_metadata?.display_name) {
        setUserName(user.user_metadata.display_name);
      } else if (user.email) {
        // Use email as fallback
        setUserName(user.email.split('@')[0]);
      }
    }
  }, [user]);

  // Track last known user id
  useEffect(() => {
    if (user?.id) lastUserIdRef.current = user.id;
  }, [user?.id]);

  // Helper to get current access token straight from Supabase (avoid stale caches)
  const getToken = async () => {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || '';
  };
  const getAuthHeaders = async () => {
    const token = await getToken();
    return token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
  };

  // Fetch Home feed (wait until user.id and token are ready). Only once per user.
  useEffect(() => {
    const controller = new AbortController();
    async function fetchFeed(page = 1, append = false) {
      try {
        if (append) setLoadingMore(true); else setLoading(true);
        setError('');
        let token = await getToken();
        if (!token) {
          // Auth not ready yet; skip for now
          return;
        }
        const forUserId = user?.id || lastUserIdRef.current;
        const userIdParam = forUserId ? `&userId=${encodeURIComponent(forUserId)}` : '';
        const res = await fetch(`/api/home/feed?page=${page}&pageSize=${feed.pageSize || 20}${userIdParam}` , {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!res.ok) {
          // If Unauthorized, attempt a single retry using a freshly read session
          if (res.status === 401) {
            const retryToken = await getToken();
            if (retryToken && retryToken !== token) {
              const retryRes = await fetch(`/api/home/feed?page=${page}&pageSize=${feed.pageSize || 20}${userIdParam}` , {
                headers: { Authorization: `Bearer ${retryToken}` },
                signal: controller.signal,
              });
              if (retryRes.ok) {
                const retryData = await retryRes.json();
                setFeed((prev) => ({
                  items: append ? [...prev.items, ...retryData.items] : retryData.items,
                  page: retryData.page,
                  pageSize: retryData.pageSize,
                  total: retryData.total,
                }));
                return;
              }
            }
            // Last resort: retry once without Authorization header to allow server-side query fallback
            const anonRes = await fetch(`/api/home/feed?page=${page}&pageSize=${feed.pageSize || 20}${userIdParam}` , {
              headers: {},
              signal: controller.signal,
            });
            if (anonRes.ok) {
              const anonData = await anonRes.json();
              setFeed((prev) => ({
                items: append ? [...prev.items, ...anonData.items] : anonData.items,
                page: anonData.page,
                pageSize: anonData.pageSize,
                total: anonData.total,
              }));
              return;
            }
          }
          const txt = await res.text();
          throw new Error(`Feed error ${res.status}: ${txt}`);
        }
        const data = await res.json();
        setFeed((prev) => ({
          items: append ? [...prev.items, ...data.items] : data.items,
          page: data.page,
          pageSize: data.pageSize,
          total: data.total,
        }));
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message || 'Failed to load feed');
      } finally {
        if (append) setLoadingMore(false); else setLoading(false);
      }
    }
    // Trigger only when we have a user.id and haven't fetched for this user yet
    if (user?.id && !fetchedRef.current.has(user.id)) {
      fetchedRef.current.add(user.id);
      fetchFeed(1);
    }
    // Listen for auth changes and refetch when session becomes available
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token) {
        localStorage.setItem('token', session.access_token);
        // refetch first page on sign-in
        fetchFeed(1);
      }
    });
    return () => {
      controller.abort();
      try {
        sub?.subscription?.unsubscribe?.();
      } catch (_) {}
    };
  }, [user?.id]);

  const hasMore = feed.items.length < feed.total;
  const loadMore = async () => {
    if (loadingMore) return;
    const nextPage = (feed.page || 1) + 1;
    const controller = new AbortController();
    // Reuse the same logic by calling the inner function through a small wrapper
    try {
      setLoadingMore(true);
      const token = await getToken();
      if (!token) return; // auth not ready
      const userIdParam = user?.id ? `&userId=${encodeURIComponent(user.id)}` : '';
      const res = await fetch(`/api/home/feed?page=${nextPage}&pageSize=${feed.pageSize || 20}${userIdParam}` , {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Feed error ${res.status}: ${txt}`);
      }
      const data = await res.json();
      setFeed((prev) => ({
        items: [...prev.items, ...data.items],
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
      }));
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };
  
  const targetPostId = (item) => (item.type === 'repost' ? item.originalPostId : item.id);

  useEffect(() => {
    async function loadShareCounts(items) {
      const uniqueIds = Array.from(new Set(items.map((it) => targetPostId(it))));
      if (!uniqueIds.length) return;
      try {
        const entries = await Promise.all(
          uniqueIds.map(async (postId) => {
            try {
              const count = await fetchShareCount(postId);
              return [postId, count];
            } catch (_) {
              return [postId, 0];
            }
          })
        );
        setShareCounts((prev) => {
          const next = { ...prev };
          entries.forEach(([id, count]) => {
            next[id] = count;
          });
          return next;
        });
      } catch (_) {
        // swallow — optional telemetry
      }
    }
    if (feed.items.length) {
      loadShareCounts(feed.items);
    }
  }, [feed.items]);

  const handlePostSubmit = async () => {
    const raw = postContent.trim();
    const { content, embedUrl } = extractEmbedFromContent(raw);
    const embed_url = embedUrl || null;
    if (!content && !postMediaFile && !embed_url) return;
    try {
      setComposerUploading(true);
      const headers = await getAuthHeaders();
      let media_url = null;
      let media_type = null;
      if (postMediaFile) {
        const ext = postMediaFile.name.split('.').pop();
        const filePath = `posts/${user.id}/${Date.now()}_${Math.random().toString(36).substr(2, 8)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('post-media')
          .upload(filePath, postMediaFile, { cacheControl: '3600', upsert: false });
        if (uploadError) throw uploadError;
        const { data: publicUrlData } = supabase.storage.from('post-media').getPublicUrl(filePath);
        media_url = publicUrlData.publicUrl;
        media_type = postMediaFile.type; // store full MIME type like image/png, video/mp4
      }
      const { data, error } = await supabase
        .from('posts')
        .insert([{
          user_id: user.id,
          owner_id: user.id,
          content,
          media_url,
          media_type,
          embed_url
        }])
        .select()
        .single();

      if (error) throw new Error(error.message);
      // Optimistically prepend new post item
      const authorName = user?.user_metadata?.display_name || user?.user_metadata?.name || (user?.email ? user.email.split('@')[0] : 'You');
      const newItem = {
        id: data.id,
        type: 'post',
        createdAt: data.created_at || new Date().toISOString(),
        source: 'profile',
        author: { id: user?.id, display_name: authorName, avatar_url: user?.user_metadata?.avatar_url },
        content: data.content,
        media_url: data.media_url,
        media_type: data.media_type,
        embed_url: data.embed_url,
        stats: { commentsCount: 0, repostsCount: 0, reactionsCount: 0, reactionsByType: {}, userReactionType: null },
      };
      setFeed((prev) => ({
        ...prev,
        items: [newItem, ...prev.items],
        total: (prev.total || 0) + 1,
      }));
      setPostContent('');
      setLocalEmbedPreview(null);
      setPostMediaFile(null);
      setPostMediaPreview(null);
      setPostMediaType(null);
      setComposerOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to post');
    } finally {
      setComposerUploading(false);
    }
  };

  // Handler for DockedComposer submission
  const handleDockedSubmit = async (content, mediaFile, mediaType, embedPreview) => {
    const { content: cleanContent, embedUrl } = extractEmbedFromContent(content);
    const embed_url = embedUrl || null;
    if (!cleanContent && !mediaFile && !embed_url) return;
    
    setComposerUploading(true);
    try {
      const headers = await getAuthHeaders();
      let media_url = null;
      let uploadedType = null;
      if (mediaFile) {
        const ext = mediaFile.name.split('.').pop();
        const filePath = `posts/${user.id}/${Date.now()}_${Math.random().toString(36).substr(2, 8)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('post-media')
          .upload(filePath, mediaFile, { cacheControl: '3600', upsert: false });
        if (uploadError) throw uploadError;
        const { data: publicUrlData } = supabase.storage.from('post-media').getPublicUrl(filePath);
        media_url = publicUrlData.publicUrl;
        uploadedType = mediaFile.type;
      }
      const { data, error } = await supabase
        .from('posts')
        .insert([{
          user_id: user.id,
          owner_id: user.id,
          content: cleanContent,
          media_url,
          media_type: uploadedType,
          embed_url
        }])
        .select()
        .single();

      if (error) throw new Error(error.message);
      // Optimistically prepend new post item
      const authorName = user?.user_metadata?.display_name || user?.user_metadata?.name || (user?.email ? user.email.split('@')[0] : 'You');
      const newItem = {
        id: data.id,
        type: 'post',
        createdAt: data.created_at || new Date().toISOString(),
        source: 'profile',
        author: { id: user?.id, display_name: authorName, avatar_url: user?.user_metadata?.avatar_url },
        content: data.content,
        media_url: data.media_url,
        media_type: data.media_type,
        embed_url: data.embed_url,
        stats: { commentsCount: 0, repostsCount: 0, reactionsCount: 0, reactionsByType: {}, userReactionType: null },
      };
      setFeed((prev) => ({
        ...prev,
        items: [newItem, ...prev.items],
        total: (prev.total || 0) + 1,
      }));
    } catch (err) {
      setError(err.message || 'Failed to post');
      throw err;
    } finally {
      setComposerUploading(false);
    }
  };

  const beginEditPost = (item) => {
    if (item.type !== 'post') return;
    setEditingPostId(item.id);
    setEditDraft({ content: item.content || '', embed_url: item.embed_url || '' });
  };

  const cancelEditPost = () => {
    setEditingPostId(null);
    setEditDraft({ content: '', embed_url: '' });
  };

  const saveEditPost = async (postId) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/home/posts/${postId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ content: editDraft.content, embed_url: editDraft.embed_url || null }),
      });
      if (!res.ok) throw new Error(`Update failed ${res.status}`);
      const updated = await res.json();
      setFeed((prev) => ({
        ...prev,
        items: prev.items.map((it) => (it.type === 'post' && it.id === postId ? { ...it, content: updated.content, embed_url: updated.embed_url } : it)),
      }));
      cancelEditPost();
    } catch (err) {
      setError(err.message || 'Failed to update post');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/home/posts/${postId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(`Delete failed ${res.status}`);
      setFeed((prev) => ({
        ...prev,
        items: prev.items.filter((it) => (it.type === 'repost' ? it.originalPostId !== postId : it.id !== postId)),
        total: Math.max(0, (prev.total || 0) - 1),
      }));
    } catch (err) {
      setError(err.message || 'Failed to delete post');
    }
  };

  // toggleReact removed — PostReactions controls reaction logic and optimistic updates.

  const toggleRepost = async (item) => {
    try {
      const headers = await getAuthHeaders();
      const postId = targetPostId(item);
      const already = !!repostedByMe[postId];
      if (already) {
        await fetch(`/api/home/posts/${postId}/repost`, { method: 'DELETE', headers });
      } else {
        await fetch(`/api/home/posts/${postId}/repost`, { method: 'POST', headers });
      }
      setRepostedByMe((prev) => ({ ...prev, [postId]: !already }));
      // Optimistic count update across references
      setFeed((prev) => ({
        ...prev,
        items: prev.items.map((it) => {
          const pid = targetPostId(it);
          if (pid !== postId) return it;
          const delta = already ? -1 : 1;
          return {
            ...it,
            stats: { ...it.stats, repostsCount: Math.max(0, (it.stats?.repostsCount || 0) + delta) },
          };
        }),
      }));
    } catch (err) {
      setError(err.message || 'Failed to repost');
    }
  };

  const handleShare = async (item) => {
    try {
      const postId = targetPostId(item);
      if (!user?.id) return;
      await addShare(postId, user.id);
      try {
        const updated = await fetchShareCount(postId);
        setShareCounts((prev) => ({ ...prev, [postId]: updated }));
      } catch (_) {
        setShareCounts((prev) => ({ ...prev, [postId]: (prev[postId] || 0) + 1 }));
      }
      const sharePayload = {
        title: 'Check out this post!',
        text: item.content || 'See what the community is sharing.',
        url: window.location.href,
      };
      if (typeof navigator !== 'undefined' && navigator.share) {
        navigator.share(sharePayload).catch(() => {});
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        navigator.clipboard.writeText(`${item.content || ''}\n${window.location.href}`).catch(() => {});
      }
    } catch (err) {
      setError(err.message || 'Failed to share');
    }
  };

  const openCommentsModal = (item) => {
    const pid = targetPostId(item);
    setActiveCommentPostId(pid);
    setCommentModalOpen(true);
  };
  
  return (
    <div className="home-section">
      <div className="welcome-banner">
        <h1>Welcome back, {userName}!</h1>
        <p>Continue your language learning journey</p>
      </div>

      {/* LinguaSnaps Row - Stories-style short videos */}
      {user && <LinguaSnapsRow />}
      
      <DockedComposer
        placeholder="Share something with the community..."
        onSubmit={handleDockedSubmit}
        isSubmitting={composerUploading}
        title="Create Post"
      />

      <div className="social-feed social-card">
        <h2>Community Feed</h2>
        <div className="feed-posts social-posts-list">
          {loading && <div className="feed-loading">Loading feed...</div>}
          {error && <div className="feed-error">{error}</div>}
          {!loading && !error && feed.items.length === 0 && (
            <div className="feed-empty">No posts to show yet. Connect with others, join groups or events to see their updates here.</div>
          )}
          {!loading && !error && feed.items.map((item) => (
            <div key={`${item.type}-${item.id}`} className="feed-card social-post-card">
              {/* Banner for group/event posts - only shows group/event info */}
              {item.source === 'group' && item.group && (
                <div className="feed-context-banner social-context-banner" style={{
                  backgroundImage: item.group.group_banner_url
                    ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${item.group.group_banner_url})`
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}>
                  <div className="banner-context-top social-context-pills">
                    <span className="banner-pill social-context-pill">Group</span>
                    <HoverCard type="group" data={item.group} currentUserId={user?.id}>
                      <a href={`/app/groups/${item.group.id}`} target="_blank" rel="noopener noreferrer" className="banner-name-link">
                        <span className="banner-name">{item.group.name || 'Group'}</span>
                      </a>
                    </HoverCard>
                  </div>
                </div>
              )}
              {item.source === 'event' && item.event && (
                <div className="feed-context-banner social-context-banner" style={{
                  backgroundImage: item.event.event_banner_url
                    ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${item.event.event_banner_url})`
                    : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                }}>
                  <div className="banner-context-top social-context-pills">
                    <span className="banner-pill social-context-pill">Event</span>
                    <HoverCard type="event" data={item.event} currentUserId={user?.id}>
                      <a href={`/app/events/${item.event.id}`} target="_blank" rel="noopener noreferrer" className="banner-name-link">
                        <span className="banner-name">{item.event.title || 'Event'}</span>
                      </a>
                    </HoverCard>
                  </div>
                </div>
              )}
              {/* Repost indicator - above the author header */}
              {item.type === 'repost' && (
                <div className="repost-indicator">
                  <FaRetweet style={{ marginRight: 6 }} />
                  <strong>{item.repostedBy?.display_name || item.repostedBy?.name || 'Someone'}</strong> reposted
                </div>
              )}
              {/* Author header - always below banner, unified structure for all posts */}
              <div className="post-header social-post-header">
                <div className="post-avatar">
                  <img
                    className="social-avatar"
                    src={item.repostedBy?.avatar_url || item.author?.avatar_url || '/avatar-placeholder.png'}
                    alt="avatar"
                  />
                </div>
                <div className="post-info social-author-meta">
                  <HoverCard type="profile" data={item.author} currentUserId={user?.id}>
                    <a href={`/app/profile/${item.author?.id}`} target="_blank" rel="noopener noreferrer" className="author-name-link">
                      <h3 className="social-author-name">{item.author?.display_name || item.author?.name || 'User'}</h3>
                    </a>
                  </HoverCard>
                  <p className="post-date social-post-date">{new Date(item.createdAt).toLocaleString()}</p>
                </div>
                {item.type === 'post' && item.author?.id === user?.id && editingPostId !== item.id && (
                  <div style={{ marginLeft: 'auto', position: 'relative' }}>
                    <button
                      type="button"
                      className="social-kebab-btn"
                      aria-label="Post options"
                      onClick={() =>
                        setOwnerMenuOpen(prev => ({
                          ...prev,
                          [item.id]: !prev[item.id]
                        }))
                      }
                    >
                      ⋮
                    </button>
                    {ownerMenuOpen[item.id] && (
                      <div className="social-kebab-menu">
                        <button
                          type="button"
                          className="social-kebab-item"
                          onClick={() => {
                            beginEditPost(item);
                            setOwnerMenuOpen(prev => ({ ...prev, [item.id]: false }));
                          }}
                        >
                          <FaEdit />
                          Edit post
                        </button>
                        <div className="social-kebab-danger-label">Danger zone</div>
                        <button
                          type="button"
                          className="social-kebab-item danger"
                          onClick={() => {
                            setOwnerMenuOpen(prev => ({ ...prev, [item.id]: false }));
                            deletePost(item.id);
                          }}
                        >
                          <FaTrash />
                          Delete post
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {editingPostId === item.id ? (
                <div className="feed-content social-post-content">
                  <textarea
                    value={editDraft.content}
                    onChange={(e) => setEditDraft((d) => ({ ...d, content: e.target.value }))}
                    placeholder="Edit your post..."
                    style={{ width: '100%', minHeight: 80 }}
                  />
                  <input
                    type="text"
                    value={editDraft.embed_url}
                    onChange={(e) => setEditDraft((d) => ({ ...d, embed_url: e.target.value }))}
                    placeholder="Embed URL (optional)"
                    style={{ width: '100%', marginTop: 8 }}
                  />
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <button className="post-button" onClick={() => saveEditPost(item.id)}>Save</button>
                    <button className="post-button" onClick={cancelEditPost}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  {item.content && <div className="feed-content social-post-content">{item.content}</div>}
                </>
              )}
              {item.media_url && (
                <div className="feed-media social-post-media">
                  {item.media_type?.startsWith('image') ? (
                    <img
                      src={item.media_url}
                      alt="media"
                      style={{ cursor: 'zoom-in' }}
                      onClick={() => { setViewer({ type: 'image', url: item.media_url, title: 'Image' }); setViewerOpen(true); }}
                    />
                  ) : item.media_type?.startsWith('video') ? (
                    <div style={{ position: 'relative' }}>
                      <video controls src={item.media_url} style={{ width: '100%' }} />
                      <button
                        className="icon-btn"
                        title="Full view"
                        aria-label="Full view"
                        onClick={() => { setViewer({ type: 'video', url: item.media_url, title: 'Video' }); setViewerOpen(true); }}
                        style={{ position: 'absolute', right: 8, bottom: 8 }}
                      >
                        <FaExpand />
                      </button>
                    </div>
                  ) : (
                    <div className="doc-preview">
                      <div className="doc-title">{item.media_url.split('/').pop()}</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <a className="icon-btn" href={item.media_url} target="_blank" rel="noreferrer" title="Open in new tab" aria-label="Open in new tab">
                          <FaExternalLinkAlt />
                        </a>
                        <button
                          className="icon-btn"
                          title="Full view"
                          aria-label="Full view"
                          onClick={() => { setViewer({ type: 'doc', url: item.media_url, title: 'Document' }); setViewerOpen(true); }}
                        >
                          <FaExpand />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {item.embed_url && (() => {
                const m = (item.embed_url || '').match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);                const ytId = m ? m[1] : null;
                const platform = getEmbedPlatform(item.embed_url);
                const menuKey = `embed-${item.id}`;
                if (ytId) {
                  return (
                    <div className="feed-media" style={{ position: 'relative' }}>
                      <img
                        src={`https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`}
                        alt="YouTube thumbnail"
                        style={{ width: '100%', borderRadius: 8, cursor: 'pointer' }}
                        onClick={() => { setViewer({ type: 'embed', url: item.embed_url, title: 'YouTube' }); setViewerOpen(true); }}
                      />
                      <button
                        className="icon-btn"
                        title="Play"
                        aria-label="Play"
                        onClick={() => { setViewer({ type: 'embed', url: item.embed_url, title: 'YouTube' }); setViewerOpen(true); }}
                        style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', borderRadius: '999px', padding: '10px 14px' }}
                      >
                        <FaPlay />
                      </button>
                      <div style={{ position: 'absolute', top: 8, right: 8 }} ref={embedMenuOpen[menuKey] ? embedMenuRef : null}>
                        <button
                          className="icon-btn"
                          onClick={() => toggleEmbedMenu(menuKey)}
                          title="Options"
                          style={{ background: 'rgba(0,0,0,0.6)', padding: '8px' }}
                        >
                          ⋮
                        </button>
                        {embedMenuOpen[menuKey] && (
                          <div className="embed-menu-dropdown" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'rgba(20,24,36,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.4)', minWidth: 180, zIndex: 1000 }}>
                            <button
                              onClick={() => copyEmbedLink(item.embed_url)}
                              style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: '#f4f6ff' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,77,255,0.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                              <FaLink /> Copy Link
                            </button>
                            <button
                              onClick={() => viewOnPlatform(item.embed_url)}
                              style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: '#f4f6ff' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,77,255,0.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                              <FaExternalLinkAlt /> View on {platform}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="embed-responsive social-post-media" style={{ position: 'relative' }}>
                    <iframe src={getEmbedSrc(item.embed_url)} title="Embed" allowFullScreen />
                    <div style={{ position: 'absolute', top: 8, right: 8 }} ref={embedMenuOpen[menuKey] ? embedMenuRef : null}>
                      <button
                        className="icon-btn"
                        onClick={() => toggleEmbedMenu(menuKey)}
                        title="Options"
                        style={{ background: 'rgba(0,0,0,0.6)', padding: '8px' }}
                      >
                        ⋮
                      </button>
                      {embedMenuOpen[menuKey] && (
                        <div className="embed-menu-dropdown" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'rgba(20,24,36,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.4)', minWidth: 180, zIndex: 1000 }}>
                          <button
                            onClick={() => copyEmbedLink(item.embed_url)}
                            style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: '#f4f6ff' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,77,255,0.2)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >
                            <FaLink /> Copy Link
                          </button>
                          <button
                            onClick={() => viewOnPlatform(item.embed_url)}
                            style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: '#f4f6ff' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,77,255,0.2)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >
                            <FaExternalLinkAlt /> View on {platform}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              <div className="feed-actions social-post-actions">
                <PostReactions
                  postId={targetPostId(item)}
                  stats={item.stats}
                  getAuthHeaders={getAuthHeaders}
                  onUpdate={(prevType, nextType) => {
                    setFeed((prev) => ({
                      ...prev,
                      items: prev.items.map((it) => {
                        const pid = targetPostId(it);
                        if (pid !== targetPostId(item)) return it;
                        const byType = { ...(it.stats?.reactionsByType || {}) };
                        let total = it.stats?.reactionsCount || 0;
                        if (prevType && !nextType) {
                          // remove
                          byType[prevType] = Math.max(0, (byType[prevType] || 1) - 1);
                          total = Math.max(0, total - 1);
                        } else if (!prevType && nextType) {
                          // add
                          byType[nextType] = (byType[nextType] || 0) + 1;
                          total = total + 1;
                        } else if (prevType && nextType && prevType !== nextType) {
                          // switch
                          byType[prevType] = Math.max(0, (byType[prevType] || 1) - 1);
                          byType[nextType] = (byType[nextType] || 0) + 1;
                        }
                        return {
                          ...it,
                          stats: {
                            ...it.stats,
                            reactionsByType: byType,
                            reactionsCount: total,
                            userReactionType: nextType || null,
                          },
                        };
                      }),
                    }));
                  }}
                />
                <button className="icon-btn" title="Repost" aria-label="Repost" onClick={() => toggleRepost(item)}>
                  <FaRetweet />
                  <span className="count-badge">{item.stats?.repostsCount ?? 0}</span>
                </button>
                <button className="icon-btn" title="Comments" aria-label="Comments" onClick={() => openCommentsModal(item)}>
                  <FaRegCommentDots />
                  <span className="count-badge">{item.stats?.commentsCount ?? 0}</span>
                </button>
                <button className="icon-btn" title="Share" aria-label="Share" onClick={() => handleShare(item)}>
                  <FaShareAlt />
                  <span className="count-badge">{shareCounts[targetPostId(item)] ?? 0}</span>
                </button>
              </div>

              <div className="feed-footer social-post-stats">
                <span>{item.stats?.reactionsCount ?? 0} reactions</span>
                <span>{item.stats?.commentsCount ?? 0} comments</span>
                <span>{item.stats?.repostsCount ?? 0} reposts</span>
                <span>{shareCounts[targetPostId(item)] ?? 0} shares</span>
              </div>
            </div>
          ))}
          {!loading && !error && hasMore && (
            <div className="feed-load-more">
              <button className="post-button" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? 'Loading…' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal for nested comments using Groups Comments component */}
      <Modal
        isOpen={commentModalOpen}
        onRequestClose={() => setCommentModalOpen(false)}
        ariaHideApp={false}
        className="forum-comment-modal"
        overlayClassName="forum-comment-modal-overlay"
      >
        <button onClick={() => setCommentModalOpen(false)} className="close-modal-x-btn" aria-label="Close modal">&#10005;</button>
        {activeCommentPostId && (
          <Comments
            postId={activeCommentPostId}
            user={user}
            onCommentAdded={() => {
              setCommentsRefreshKey((k) => k + 1);
              // Optimistically bump the count on the active item
              setFeed((prev) => ({
                ...prev,
                items: prev.items.map((it) =>
                  (it.type === 'repost' ? it.originalPostId : it.id) === activeCommentPostId
                    ? { ...it, stats: { ...it.stats, commentsCount: (it.stats?.commentsCount || 0) + 1 } }
                    : it
                ),
              }));
            }}
            replyTo={replyToCommentId}
            openReplyModal={(id) => setReplyToCommentId(id)}
          />
        )}
      </Modal>

      {/* Full-view media/document/embed viewer */}
      <Modal
        isOpen={viewerOpen}
        onRequestClose={() => setViewerOpen(false)}
        ariaHideApp={false}
        style={{
          overlay: { backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1200 },
          content: {
            inset: '10% 8% 10% 8%', background: 'var(--card-bg)', border: '1px solid var(--border-light)',
            borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column'
          }
        }}
        contentLabel="Viewer"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontWeight: 700 }}>{viewer.title || 'Preview'}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {viewer?.url ? (
              <a className="icon-btn" href={viewer.url} target="_blank" rel="noreferrer" title="Open in new tab" aria-label="Open in new tab">
                <FaExternalLinkAlt />
              </a>
            ) : null}
            <button className="icon-btn" onClick={() => setViewerOpen(false)} title="Close" aria-label="Close">✖</button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {viewer.type === 'image' && (
            <img src={viewer.url} alt="full" style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }} />
          )}
          {viewer.type === 'video' && (
            <video controls src={viewer.url} style={{ width: '100%', height: 'auto' }} />
          )}
          {viewer.type === 'doc' && (
            <iframe title="document" src={viewer.url} style={{ width: '100%', height: '80vh', border: 0 }} />
          )}
          {viewer.type === 'embed' && (
            <div className="embed-responsive">
              <iframe src={getEmbedSrc(viewer.url)} title="Embed" allowFullScreen />
            </div>
          )}
        </div>
      </Modal>

    </div>
  );
};

export default Home;
